import React, { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/therapist/Button';
import { ScrollView, View, Modal as RNModal, Dimensions } from 'react-native';
import { Divider } from 'react-native-elements';
import encryptedStorage from '@/stores/encryptedStorage';
import { ExerciseButton } from '@/views/SetCard/ExerciseButton';
import { fetchPatientsList, getPermissions } from '@/api/services/user';
import { hasInternetAsync } from '@/screens/HomeTherapist/helpers';
import { IS_IOS, IS_WEB } from '@/utils/platform';
import { logError } from '@/utils/logHandling';
import { logInfo } from '@/utils/logHandling';
import { Modal } from '@/components/ui/therapist/Modal';
import { NeolexonIcon } from '@/components/NeolexonIcon';
import { Patient, SetType, WordSet } from '@/stores/store.types';
import { PressableNeolexon } from '@/components/PressableNeolexon';
import { StackNavigationProp } from '@react-navigation/stack';
import { Text } from '@/components/ui/therapist/Text';
import { TherapistStackParamList } from '@/navigations/TherapistStack';
import { PREPARED_DIALOGS } from '@/components/ui/shared/Dialog/constants';
import { useDashboardContext } from '@/contexts/DashboardContext/useDashboardContext';
import { SetEditorFlow } from '@/contexts/SetEditorContext';
import { useSetEditor } from '@/contexts/SetEditorContext/useSetEditor';
import { useDialog } from '@/hooks/useDialog';
import { useNavigation } from '@react-navigation/native';
import { useNotification } from '@/components/ui/therapist/Notification/NotificationProvider';
import { useStoreActions, useStoreState } from '@/stores/store';
import { useOnHover } from '@/hooks/useOnHover';
import { API_NAME_ACCORD_TYPE } from '@/views/SetCard/constants';
import colors from '@/config/colors';
import theme from '@/config/theme';
import { styles } from './styles';
import { setStorageKey } from '@/utils/setStorageKey';
import { useSetsMode } from '@/contexts/SetsModeContext/useSetsMode';

const ACTIVE_SETS_TYPE = {
  Wörtersets: 'activeWordsets',
  Sätzesets: 'activeSentencesets',
  Textesets: 'activeTextsets',
};

type MenuItem = {
  label: string;
  action: OnActionType;
  isDisabled?: boolean;
  isDestructive?: boolean;
};

export type OnActionType =
  | 'trash'
  | 'pencil'
  | 'copy'
  | 'results'
  | 'template'
  | 'distract'
  | 'duplicate';

type SetCardMenuProps = {
  appDisplayName: string;
  exerciseSetName: string;
  id: string;
  patientID: string;
  setDeleteModal: (value: { isSetActive: boolean; show: boolean }) => void;
  type: SetType;
};

export const SetCardMenu: React.FC<SetCardMenuProps> = ({
  appDisplayName,
  exerciseSetName,
  id,
  patientID,
  setDeleteModal,
  type,
}) => {
  const { handleHoverIn, handleHoverOut } = useOnHover();
  const { fetchPatients, patientsSyncing, selectedPatient } =
    useDashboardContext();
  const { isTherapistDemo, permissions } = useStoreState((store) => store);
  const { showNotification } = useNotification();
  const { showDialog } = useDialog();
  const { onFirstMountStoreSetup } = useStoreActions((actions) => ({
    onFirstMountStoreSetup: actions.onFirstMountStoreSetup,
  }));
  const { navigate } =
    useNavigation<StackNavigationProp<TherapistStackParamList>>();
  const { resetFormData } = useSetEditor();
  const [menuPosition, setMenuPosition] = useState<{
    bottom: number;
    right: number;
  }>({ bottom: 0, right: 0 });
  const [ergebnisseTherapistDemo, setErgebnisseTherapistDemo] =
    useState<boolean>(false);
  const [showSetCardMenu, setShowCardMenu] = useState<boolean>(false);
  const ellipsisRef = useRef<View>(null);
  const pendingActionRef = useRef<OnActionType | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const { refresh } = useStoreState((state) => state);
  const { downloadedExercises } = useDashboardContext();
  const { mode } = useSetsMode();

  useEffect(() => {
    let isMounted = true;
    encryptedStorage()
      .getItem(setStorageKey(id, type))
      .then((localSet: any) => {
        if (!isMounted) {
          return;
        }
        setIsLoaded(!!localSet?.activeWordset);
      });
    return () => {
      isMounted = false;
    };
  }, [id, refresh, downloadedExercises]);

  const openSetEditor = (flow: SetEditorFlow) => {
    resetFormData();
    const params = {
      selectedID: patientID,
      setId: id,
      apiName: API_NAME_ACCORD_TYPE[type],
      flow,
    };

    switch (type) {
      case 'Wörtersets':
        navigate('WordSet', params);
        return;
      case 'Sätzesets':
        navigate('SentenceSet', params);
        return;
      case 'Textesets':
        navigate('TextSet', params);
        return;
    }
  };

  const navigateToSet = () => openSetEditor('editSet');

  const navigateToTemplate = () => {
    if (type === 'Textesets') {
      return;
    }
    openSetEditor('templateFromSet');
  };

  const onActionPress = async (name: OnActionType) => {
    const internet = await hasInternetAsync();
    if (!internet) {
      setShowCardMenu(false);
      setTimeout(() => showDialog(PREPARED_DIALOGS.noInternetDialog()), 200);
      return;
    }
    if (IS_IOS && showSetCardMenu) {
      pendingActionRef.current = name;
      setShowCardMenu(false);
      return;
    }
    setShowCardMenu(false);
    switch (name) {
      case 'trash': {
        if (isTherapistDemo) {
          showNotification({
            type: 'error',
            title: 'Löschen in der Demo nicht möglich',
            position: 'top',
          });
        } else {
          const activeSets =
            selectedPatient?.[ACTIVE_SETS_TYPE[type] as keyof Patient] ?? null;
          const isActiveSet =
            Array.isArray(activeSets) && (activeSets as string[]).includes(id);
          setDeleteModal({
            show: true,
            isSetActive: isActiveSet,
          });
        }
        break;
      }
      case 'pencil': {
        navigateToSet();
        break;
      }
      case 'template': {
        if (isTherapistDemo) {
          showNotification({
            type: 'error',
            title: 'Vorlage speichern in der Demo nicht möglich',
            position: 'top',
          });
        } else {
          navigateToTemplate();
        }
        break;
      }
      case 'results': {
        let className = '';
        let classSet = '';
        if (type == 'Wörtersets') {
          classSet = 'Wörterset';
          className = 'wordset';
        }
        if (type == 'Sätzesets') {
          classSet = 'Sätzeset';
          className = 'sentenceset';
        }
        if (isTherapistDemo) {
          setErgebnisseTherapistDemo(true);
        } else {
          // @ts-ignore
          navigate('Ergebnisse', {
            exerciseSetName,
            exerciseSet: classSet,
            exerciseClass: className,
            setId: id,
            patientName:
              selectedPatient?.firstName + ' ' + selectedPatient?.lastName,
            patientId: patientID,
            appDisplayName: appDisplayName,
          });
        }
        break;
      }
      case 'copy': {
        if (isTherapistDemo) {
          showNotification({
            type: 'error',
            title: 'Kopieren in der Demo nicht möglich',
            position: 'top',
          });
          break;
        } else {
          navigate('CopySet', {
            setCopyFromPatientId: patientID,
            setId: id,
            setName: exerciseSetName,
            setType: type,
          });
        }
        break;
      }
      case 'duplicate': {
        if (isTherapistDemo) {
          showNotification({
            type: 'error',
            title: 'Neues Set aus diesem Set in der Demo nicht möglich',
            position: 'top',
          });
          break;
        } else {
          openSetEditor('createSet');
        }
        break;
      }
      case 'distract': {
        try {
          const storageKey = setStorageKey(id, type);
          const res = await encryptedStorage().getItem(storageKey);
          const { activeWordset, ...rest } = res as WordSet;
          await encryptedStorage().setItem(storageKey, rest);
          const objFetchPatientList = await fetchPatientsList(true);
          await fetchPatients(selectedPatient, objFetchPatientList.patients);
          await patientsSyncing(objFetchPatientList.patients, true);
          const objPermission = await getPermissions(true);
          showNotification({
            type: 'info',
            title: 'Neue Ablenker wurden generiert.',
          });
          onFirstMountStoreSetup({
            appStartResumeLock: objFetchPatientList.appStartResumeLock,
            eigentrainingLock: objFetchPatientList.eigentrainingLock,
            appLockPattern: objFetchPatientList.appLockPattern,
            patients: objFetchPatientList.patients,
            subscriptions: objPermission.subscriptions,
            permissions: objPermission.permissions,
          });
        } catch (error) {
          logError('Neue Ablenker in SetCardOptions ', error);
        }
        break;
      }
      default: {
        logInfo('ACTION BTN PRESSED');
        break;
      }
    }
  };
  const canStartPracticing = !!permissions?.exercises;

  const canPractice = !!(permissions?.exercises || isTherapistDemo);
  const isTextesets = type === 'Textesets';

  let canStartTraining = true;
  if (IS_WEB) {
    const currentUrlParams = new URLSearchParams(window.location.search);
    canStartTraining =
      currentUrlParams.get('nur-eigentraining-einstellen') === null;
  }

  const isDownloaded = isLoaded;

  const universalItems: MenuItem[] = [
    {
      label: 'Set bearbeiten',
      action: 'pencil' as OnActionType,
      isDisabled: false,
    },
    {
      label: 'Weiteren Patient:innen zuweisen',
      action: 'copy' as OnActionType,
      isDisabled: false,
    },
    {
      label: 'Duplizieren',
      action: 'duplicate' as OnActionType,
      isDisabled: isTherapistDemo,
    },
    {
      label: 'Löschen',
      action: 'trash' as OnActionType,
      isDestructive: true,
      isDisabled: false,
    },
  ];

  const menuItems: MenuItem[] = !canStartTraining
    ? [
        ...(!isTextesets
          ? [
              ...(!canStartPracticing
                ? [
                    {
                      label: 'Ergebnisse anzeigen',
                      action: 'results' as OnActionType,
                    },
                  ]
                : []),
              {
                label: 'Set als Vorlage speichern',
                action: 'template' as OnActionType,
              },
            ]
          : []),
        ...universalItems,
      ]
    : [
        ...(!isTextesets && canPractice
          ? [
              {
                label: 'Neue Ablenker',
                action: 'distract' as OnActionType,
                isDisabled: !isDownloaded,
              },
            ]
          : []),
        ...(!isTextesets && canStartPracticing
          ? [
              {
                label: 'Ergebnisse anzeigen',
                action: 'results' as OnActionType,
              },
            ]
          : []),
        ...(!isTextesets
          ? [
              {
                label: 'Set als Vorlage speichern',
                action: 'template' as OnActionType,
              },
            ]
          : []),
        ...universalItems,
      ];

  return (
    <>
      <ExerciseButton
        canStartPracticing={!!(canStartPracticing && canStartTraining)}
        id={id}
        onActionPress={(action) => {
          setShowCardMenu(false);
          onActionPress(action);
        }}
        type={type}
      />
      {mode === 'aufgaben' ? (
        <PressableNeolexon
          testID={`set-card-menu-${exerciseSetName}`}
          onHoverIn={handleHoverIn}
          onHoverOut={handleHoverOut}
          ref={ellipsisRef}
          style={styles.ellipsisBtn}
          onPress={() => {
            ellipsisRef.current?.measureInWindow((x, y, width, height) => {
              setMenuPosition({
                right: Dimensions.get('window').width - (x + width),
                bottom: Dimensions.get('window').height - (y + height),
              });
              setShowCardMenu(!showSetCardMenu);
            });
          }}
        >
          <NeolexonIcon name="ellipsis" size={40} />
        </PressableNeolexon>
      ) : null}
      <RNModal
        transparent
        visible={showSetCardMenu}
        onDismiss={() => {
          if (pendingActionRef.current) {
            onActionPress(pendingActionRef.current);
            pendingActionRef.current = null;
          }
        }}
        supportedOrientations={[
          'landscape',
          'landscape-left',
          'landscape-right',
        ]}
      >
        <PressableNeolexon
          style={{ flex: 1 }}
          onPress={() => setShowCardMenu(!showSetCardMenu)}
        />
        <View
          style={[
            styles.menuWrapper,
            {
              right: menuPosition.right,
              bottom: Math.max(menuPosition.bottom, theme.spacings.sm),
              maxHeight: Dimensions.get('window').height * 0.6,
            },
          ]}
        >
          <View
            style={[
              styles.menuButtonWrapper,
              {
                borderColor: colors.newUI.therapist.brandGreen,
              },
            ]}
          >
            <ScrollView bounces={false}>
              {menuItems.map((item, index) => (
                <React.Fragment key={item.action}>
                  <PressableNeolexon
                    testID={`set-menu-${item.action}`}
                    onPress={() =>
                      !item.isDisabled && onActionPress(item.action)
                    }
                    style={{
                      padding: theme.spacings.md,
                      borderTopLeftRadius:
                        index === 0 ? theme.borderRadii.lg : 0,
                      borderTopRightRadius:
                        index === 0 ? theme.borderRadii.lg : 0,
                      borderBottomLeftRadius:
                        index === menuItems.length - 1
                          ? theme.borderRadii.lg
                          : 0,
                      borderBottomRightRadius:
                        index === menuItems.length - 1
                          ? theme.borderRadii.lg
                          : 0,
                      opacity: item.isDisabled ? 0.35 : 1,
                    }}
                  >
                    <Text
                      style={{
                        textAlign: 'right',
                        color: item.isDestructive
                          ? 'red'
                          : colors.newUI.therapist.textBlue100,
                      }}
                    >
                      {item.label}
                    </Text>
                  </PressableNeolexon>
                  {index < menuItems.length - 1 && (
                    <Divider color={colors.newUI.therapist.blue20} />
                  )}
                </React.Fragment>
              ))}
            </ScrollView>
          </View>
        </View>
      </RNModal>

      <Modal
        onClose={() => setErgebnisseTherapistDemo(false)}
        isVisible={ergebnisseTherapistDemo}
        title="Ergebnisse in der Demo nicht verfügbar"
        confirmActionButton={
          <Button
            label="OK"
            onPress={() => setErgebnisseTherapistDemo(!ergebnisseTherapistDemo)}
          />
        }
      >
        <Text style={{ fontSize: 18 }}>
          Registrieren Sie sich bei neolexon, um Ihre eigenen Patient:innen und
          Übungssets zu erstellen.
        </Text>
      </Modal>
    </>
  );
};
